import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { type ManagementBuildingRow } from '../../../../models/peerToPeer/Management'
import { BuildingActions } from './BuildingActions'
import { StatusChip } from './StatusChip'
import { tableStyles } from '../../../customComponents/dataTable/tableStyles'
import { SortCaretsIcon } from '../../../customComponents/dataTable/SortCaretsIcon'
import { styles } from './BuildingTable.styles'

const HEADERS = ['Submitted By', 'ISD', 'District', 'Building', 'Status']

const asTime = (value: string | null): number => value != null ? new Date(value).getTime() : 0

interface Props {
  rows: ManagementBuildingRow[]
  reportingYearId: number
  onChanged: () => void
}

export const BuildingTable = ({ rows, reportingYearId, onChanged }: Props): JSX.Element => {
  const [newestFirst, setNewestFirst] = useState(true)

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => newestFirst
      ? asTime(b.lastUpdatedAt) - asTime(a.lastUpdatedAt)
      : asTime(a.lastUpdatedAt) - asTime(b.lastUpdatedAt)),
    [rows, newestFirst]
  )

  return (
    <Box sx={tableStyles.container}>
      <Table data-testid='p2pBuildingTable'>
        <TableHead>
          <TableRow>
            {HEADERS.map(header => <TableCell key={header} sx={tableStyles.headCell}>{header}</TableCell>)}
            <TableCell
              sx={styles.sortableHeadCell} data-testid='p2pSortLastUpdated'
              onClick={() => { setNewestFirst(!newestFirst) }}
            >
              <Box component='span' sx={tableStyles.headLabel}>
                Last Updated
                <SortCaretsIcon direction={newestFirst ? 'desc' : 'asc'} />
              </Box>
            </TableCell>
            <TableCell sx={tableStyles.headCell}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => (
            <TableRow key={row.formId ?? `nb-${index}`} data-testid={`p2pFormRow-${row.formId ?? index}`} sx={tableStyles.row}>
              <TableCell sx={tableStyles.bodyCell}>
                <Typography variant='body1' sx={styles.submitterName}>{row.submittedByName}</Typography>
                {row.submittedByEmail !== '' && <Typography variant='body2' sx={styles.submitterEmail}>{row.submittedByEmail}</Typography>}
              </TableCell>
              <TableCell sx={tableStyles.bodyCell}>{row.isd}</TableCell>
              <TableCell sx={tableStyles.bodyCell}>{row.district}</TableCell>
              <TableCell sx={tableStyles.bodyCell}>{row.building}</TableCell>
              <TableCell sx={tableStyles.bodyCell}><StatusChip status={row.status} /></TableCell>
              <TableCell sx={tableStyles.bodyCell}>{row.lastUpdatedAt != null ? new Date(row.lastUpdatedAt).toLocaleDateString() : '—'}</TableCell>
              <TableCell sx={tableStyles.bodyCell}>
                <BuildingActions
                  formId={row.formId} districtOrganizationId={row.districtOrganizationId}
                  reportingYearId={reportingYearId} onChanged={onChanged}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
